// Mock data for the Cover tab — the team's leave and who picks up each circle
// while someone is away. Stands in for the cover-schedule API. Names are drawn
// from the care circles index (fixtures.ts) so the two tabs describe the same
// people.

import { CARE_CIRCLES, SIGNED_IN_CAM } from '@/cam-admin/care-circles/fixtures';

// A colleague who can cover a circle. First name only, as the Figma's per-row
// Cover picker shows.
export const COVER_OPTIONS = ['Amara', 'Abbie', 'Priya', 'James'] as const;

// Colleagues who can be booked as the main cover — full names, as the Schedule
// cover drawer's Main cover picker shows.
export const MAIN_COVER_OPTIONS = [
  'Amara Osei',
  'Abbie Lawson',
  'Priya Nair',
  'James Okonkwo',
];

export type CoverStatus = 'active' | 'upcoming' | 'past';

// One circle's cover during a period: who's cared for, their primary carer, and
// the colleague picking it up (defaults to the period's main cover).
export type CoverAssignment = {
  careRecipient: string;
  primaryCarer: string;
  cover: string;
};

export type CoverPeriod = {
  id: string;
  // Whose leave this is. The tab shows the whole team's, so a period has to say
  // who it belongs to: only its owner can edit or cancel it, and 'you are the
  // cover' is the owner being someone else.
  owner: string;
  // ISO dates ('2026-07-08') — the display range is derived with formatCoverRange
  // and overlaps are checked on these.
  start: string;
  end: string;
  // The colleague holding the CAM's circles overall while they're away. Circles
  // without their own cover fall back to this person.
  mainCover: string;
  status: CoverStatus;
  // What the CAM wants their cover to know across all circles.
  handoverNote?: string;
  assignments: CoverAssignment[];
};

// ISO range → the '8 – 15 July 2026' display used on the card. Same-month ranges
// collapse the month; cross-month ranges show both.
export const formatCoverRange = (start: string, end: string) => {
  const s = new Date(`${start}T00:00:00`);
  const e = new Date(`${end}T00:00:00`);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '';
  const longMonth = (d: Date) =>
    d.toLocaleDateString('en-GB', { month: 'long' });
  const shortMonth = (d: Date) =>
    d.toLocaleDateString('en-GB', { month: 'short' });
  if (start === end) return `${s.getDate()} ${longMonth(s)} ${e.getFullYear()}`;
  const sameMonth =
    s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  if (sameMonth) {
    return `${s.getDate()} – ${e.getDate()} ${longMonth(e)} ${e.getFullYear()}`;
  }
  const sameYear = s.getFullYear() === e.getFullYear();
  const sPart = `${s.getDate()} ${shortMonth(s)}${sameYear ? '' : ` ${s.getFullYear()}`}`;
  return `${sPart} – ${e.getDate()} ${shortMonth(e)} ${e.getFullYear()}`;
};

// Inclusive day count — 8 to 15 July is 8 days, not 7. The table shows it
// beside the range so a glance says how long somebody is gone for without
// doing the arithmetic.
export const coverDuration = (start: string, end: string) => {
  const s = new Date(`${start}T00:00:00`);
  const e = new Date(`${end}T00:00:00`);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '';
  const days = Math.round((e.getTime() - s.getTime()) / 86_400_000) + 1;
  return days === 1 ? '1 day' : `${days} days`;
};

// Does [start, end] clash with any existing period (excluding the one being
// edited)? ISO date strings compare correctly as strings.
export const periodsOverlap = (
  start: string,
  end: string,
  periods: CoverPeriod[],
  excludeId?: string,
) =>
  periods.some((p) => p.id !== excludeId && start <= p.end && p.start <= end);

// Each circle's cover. Every circle defaults to the period's main cover; a row
// only differs when the CAM has picked a different colleague for it.
const FULL_ASSIGNMENTS: CoverAssignment[] = [
  {
    careRecipient: 'Eleanor Qualley',
    primaryCarer: 'Margarette Qualley',
    cover: 'Abbie',
  },
  { careRecipient: 'Grace Osei', primaryCarer: 'David Osei', cover: 'Amara' },
  {
    careRecipient: 'Robert Whitfield',
    primaryCarer: 'Hannah Whitfield',
    cover: 'Amara',
  },
  {
    careRecipient: 'Morag Fraser',
    primaryCarer: 'Callum Fraser',
    cover: 'Amara',
  },
  {
    careRecipient: 'Margaret Reid',
    primaryCarer: 'Thomas Reid',
    cover: 'Abbie',
  },
];

const circlesOf = (caseManager: string, cover: string): CoverAssignment[] =>
  CARE_CIRCLES.filter((c) => c.caseManager?.name === caseManager).map((c) => ({
    careRecipient: c.careRecipient,
    primaryCarer: c.carer,
    cover,
  }));

export const COVER_PERIODS: CoverPeriod[] = [
  {
    id: 'cp-active',
    owner: SIGNED_IN_CAM,
    start: '2026-07-08',
    end: '2026-07-15',
    mainCover: 'Amara Osei',
    status: 'active',
    handoverNote:
      'I’m off for a week. Amara is holding everything — flag anything urgent to her. Margarette’s funding call on the 17th is the one to watch.',
    assignments: FULL_ASSIGNMENTS,
  },
  {
    id: 'cp-up-1',
    owner: SIGNED_IN_CAM,
    start: '2026-09-03',
    end: '2026-09-10',
    mainCover: 'Amara Osei',
    status: 'upcoming',
    handoverNote:
      'A short break. Amara has the lot unless I’ve picked someone else for a circle.',
    assignments: FULL_ASSIGNMENTS,
  },
  {
    id: 'cp-up-2',
    owner: SIGNED_IN_CAM,
    start: '2026-10-21',
    end: '2026-10-28',
    mainCover: 'Priya Nair',
    status: 'upcoming',
    assignments: FULL_ASSIGNMENTS.slice(0, 3),
  },
  {
    id: 'cp-past',
    owner: SIGNED_IN_CAM,
    start: '2026-04-02',
    end: '2026-04-09',
    mainCover: 'Amara Osei',
    status: 'past',
    assignments: FULL_ASSIGNMENTS.slice(0, 4),
  },
  {
    id: 'cp-priya',
    owner: 'Priya Nair',
    start: '2026-09-07',
    end: '2026-09-14',
    mainCover: SIGNED_IN_CAM,
    status: 'active',
    handoverNote:
      'Away for a week. The Fraser funding decision is due while I’m off.',
    assignments: circlesOf('Priya Nair', SIGNED_IN_CAM),
  },
  {
    id: 'cp-james',
    owner: 'James Okonkwo',
    start: '2026-09-28',
    end: '2026-10-02',
    mainCover: SIGNED_IN_CAM,
    status: 'upcoming',
    assignments: circlesOf('James Okonkwo', SIGNED_IN_CAM),
  },
  {
    id: 'cp-amara',
    owner: 'Amara Osei',
    start: '2026-11-02',
    end: '2026-11-13',
    mainCover: 'Priya Nair',
    status: 'upcoming',
    assignments: [],
  },
];

// A brand-new period's cover: every circle defaults to the chosen main cover.
export const defaultAssignments = (mainCover: string): CoverAssignment[] =>
  CARE_CIRCLES.map((c) => ({
    careRecipient: c.careRecipient,
    primaryCarer: c.carer,
    cover: mainCover.split(' ')[0],
  }));
